import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "Are you sure?",
  description: "",
  confirmText: "Delete",
  cancelText: "Cancel",
  destructive: true,
};

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULTS);
  const resolverRef = useRef(null);

  // confirm(message) or confirm({ title, description, confirmText, cancelText, destructive })
  const confirm = useCallback((arg) => {
    const options = typeof arg === "string" ? { description: arg } : (arg || {});
    setOpts({ ...DEFAULTS, ...options });
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) settle(false); }}>
        <AlertDialogContent className="bg-emerald-surface border-gold-subtle text-white" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl text-white">{opts.title}</AlertDialogTitle>
            {opts.description && (
              <AlertDialogDescription className="text-emerald-muted">{opts.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => settle(false)}
              className="bg-transparent border-gold-subtle text-emerald-muted hover:text-white hover:bg-emerald-elev/60 mt-0"
              data-testid="confirm-cancel">
              {opts.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className={opts.destructive
                ? "bg-red-600 text-white hover:bg-red-500 border-0"
                : "gold-button border-0"}
              data-testid="confirm-accept">
              {opts.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

// Returns confirm(messageOrOptions) => Promise<boolean>
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback to native confirm if provider is missing, so callers never crash.
    return (arg) => Promise.resolve(window.confirm(typeof arg === "string" ? arg : (arg?.description || "Are you sure?")));
  }
  return ctx;
}
